import { Component, Input, OnDestroy, ViewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { NotificationsService } from 'angular2-notifications';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { TicketReminderDTO } from '@sb-tools/typescript/models/dto/ticket-reminder.dto';
import { TicketService } from '../../../core/services/ticket/ticket.service';
import { InlineSVGComponent } from '../../../../sb-modules/Frontend-Inline-SVG-Module/inline-svg.component';
import { NgClass } from '@angular/common';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { FocusDirective } from '../../../../sb-tools/typescript/directives/focus.directive';
import { TimepickerModule } from 'ngx-bootstrap/timepicker';

@Component({
    selector: 'app-reminder-modal',
    templateUrl: './reminder-modal.component.html',
    styleUrls: ['./reminder-modal.component.scss'],
    imports: [
        TranslateModule,
        InlineSVGComponent,
        FormsModule,
        NgClass,
        BsDatepickerModule,
        FocusDirective,
        TimepickerModule,
    ],
})
export class ReminderModalComponent implements OnDestroy {
    @ViewChild('modalForm') public form: NgForm;

    @Input() ticketId: number;
    @Input() replyId: number;
    @Input() hasReopenOption = true;

    confirmCallback: (reminder: TicketReminderDTO) => void;
    date: Date;
    dateTime: Date;
    isSending = false;
    isSubmitted = false;
    isTimeValid = true;
    isReopenTicket = false;
    time: Date;
    todayDate = new Date();

    private unsubscriber = new Subject<void>();

    constructor(
        private modalRef: BsModalRef,
        private ticketService: TicketService,
        private notificationsService: NotificationsService,
        private translateService: TranslateService,
    ) {
        this.date = new Date();
        this.time = new Date(new Date().getTime() + 60000 * 15);
    }

    confirm(): void {
        this.isSubmitted = true;
        if (!this.form.valid || !this.date || this.date.toString() === 'Invalid Date') {
            return;
        }
        this.dateTime = this.getDateTime(this.date, this.time);
        if (this.dateTime < this.todayDate) {
            return;
        }
        const reminder: TicketReminderDTO = {
            time: this.dateTime,
        };

        if (this.hasReopenOption) {
            reminder.isSetTicketStatusToOpenAfterRemind = this.isReopenTicket;
        }

        if (this.replyId) {
            reminder.replyId = this.replyId;
        }

        this.isSending = true;

        this.ticketService
            .addReminder(this.ticketId, reminder)
            .pipe(
                finalize(() => {
                    this.isSending = false;
                }),
                takeUntil(this.unsubscriber),
            )
            .subscribe((addedReminder: TicketReminderDTO) => {
                this.confirmCallback(addedReminder);
                this.notificationsService.success(
                    this.translateService.instant('Success!'),
                    this.translateService.instant('Reminder was successfully added.'),
                );
                this.modalRef.hide();
            });
    }

    decline(): void {
        this.modalRef.hide();
    }

    updateDate(): void {
        this.dateTime = this.getDateTime(this.date, this.time);
    }

    updateTime(time: Date): void {
        this.dateTime = this.getDateTime(this.date, time);
    }

    private getDateTime(date: Date, time: Date): Date {
        if (date == null) {
            date = new Date();
            this.date = date;
        }
        if (time == null) {
            time = new Date();
        }
        return new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            time.getHours(),
            time.getMinutes(),
            time.getSeconds(),
            time.getMilliseconds(),
        );
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
